<template>
  <div class="standard-page v-flex">
    <div class="page-contents">
      <ValidationObserver
        tag="form"
        ref="observerRef"
        class="view-form"
        @submit.prevent="onSubmit"
        v-slot="{ errors, invalid }"
      >
        <div class="fields">
          <TextInput
            name="first_name"
            label="First Name"
            v-model="first_name"
            :errors="errors"
            rules="required|lessThan:20"
            :maxlength="20"
          />
          <TextInput
            name="last_name"
            label="Last Name"
            v-model="last_name"
            :errors="errors"
            rules="required|lessThan:20"
            :maxlength="20"
          />
          <TextInput
            name="email"
            label="Email"
            v-model="email"
            :errors="errors"
            rules="required|validEmail|lessThan:55"
            :maxlength="55"
          />
          <TextInput
            name="sms_phone_number"
            label="Phone"
            v-model="sms_phone_number"
            :errors="errors"
            :rules="phoneRules"
            type="phone"
          />
          <TextInput
            name="address"
            label="Address"
            v-model="address"
            :errors="errors"
            rules="required|lessThan:100"
            :maxlength="100"
          />
          <TextInput
            name="city"
            label="City"
            v-model="city"
            :errors="errors"
            rules="required|lessThan:30"
            :maxlength="30"
          />
          <SelectInput
            name="state_province"
            label="State/Province"
            v-model="state_province"
            :errors="errors"
            :options="states"
            rules="required"
          />
          <TextInput
            name="postal_code"
            label="Postal Code"
            v-model="postal_code"
            :errors="errors"  
            rules="required|lessThan:10|greaterThan:5"
            :maxlength="10"
          />
          <SelectInput
            v-if="show_merchant"
            name="merchant_account"
            label="Merchant Account ID"
            v-model="merchant_account"
            :disabled="merchant_accounts.length <= 1"
            :errors="errors"
            :options="merchant_accounts"
          />
          <TextInput
            name="amount_due"
            label="Amount Due"
            v-model.number="amount_due"
            rules="validNumber"
            prepend="$"
            :errors="errors"
          />
          <TextInput
            name="due_date"
            label="Due Date"
            v-model="due_date"
            :errors="errors"
            rules="presentOrFuture"
            type="date"
          />
          <TextInput
            name="minimum_payment"
            label="Minimum Payment"
            v-model.number="minimum_payment"
            rules="validNumber"
            prepend="$"
            :errors="errors"
          />
          <TextInput
            name="maximum_payment"
            label="Maximum Payment"
            v-model.number="maximum_payment"
            rules="validNumber"
            prepend="$"
            :errors="errors"
          />
        </div>

        <LineBreak />

        <div class="form-actions fields">
          <b-button @click="onCancel" size="sm" variant="outline-danger">
            Cancel
          </b-button>
          <b-button size="sm" type="submit" :disabled="invalid">
            Create Customer
          </b-button>
        </div>
      </ValidationObserver>
    </div>
    <b-modal id="modal" hide-footer size="sm" centered title="Customer Created">
      <b-container fluid class="text-center">
        <b-button-group vertical>
          <b-button @click="viewCustomer">VIEW CUSTOMER LIST</b-button>
          <b-button @click="createCustomer">CREATE NEXT CUSTOMER</b-button>
        </b-button-group>
      </b-container>
    </b-modal>
  </div>
</template>

<script>
import LineBreak from '@/components/Elements/LineBreak/LineBreak.vue'
import axios from '@/utils/init-axios'
import endpoints from '@/utils/endpoints.js'
import customerCreateValidation from '@/views/Customers/CustomerCreateView/customerCreateValidation.ts'
import TextInput from '@/components/FormInputs/TextInput/TextInput.vue'
import SelectInput from '@/components/FormInputs/SelectInput/SelectInput.vue'
import { phoneReformatter } from '@/utils/formatters.ts'

export default {
  components: {
    LineBreak,
    TextInput,
    SelectInput
  },
  data() {
    return {
      email: '',
      first_name: '',
      last_name: '',
      sms_phone_number: null,
      address: '',
      city: '',
      state_province: '',
      postal_code: '',
      show_merchant: false,
      amount_due: '',
      merchant_account: '',
      merchant_accounts: [],
      due_date: '',
      minimum_payment: null,
      maximum_payment: null
    }
  },
  mounted() {
    this.$store.dispatch('setPageTitle', { pageTitle: 'Create Customer' })
    this.getMerchantAccounts()
  },
  methods: {
    onCancel() {
      this.$router.push({ name: 'CustomerList' })
    },
    onSubmit(event) {
      let payload = {
        email: this.email,
        first_name: this.first_name,
        last_name: this.last_name,
        sms_phone_number: phoneReformatter(this.sms_phone_number),
        address: this.address,
        city: this.city,
        state_province: this.state_province,
        postal_code: this.postal_code,
        accounts: [
          {
            amount_due: this.amount_due,
            merchant_account: this.merchant_account,
            due_date: this.due_date,
            minimum_payment: this.minimum_payment,
            maximum_payment: this.maximum_payment
          }
        ]
      }
      if (this.minimum_payment) {
        payload.accounts[0].minimum_payment = this.minimum_payment
      }
      if (this.maximum_payment) {
        payload.accounts[0].maximum_payment = this.maximum_payment
      }
      axios
        .post(endpoints.createCustomer(this.$route.params.merchantID), payload)
        .then(res => {
          event.target.reset()
          this.$bvModal.show('modal')
        })
        .catch(err => {
          console.log(err)
          for(let error in this.$refs.observerRef.errors) {
            // loads response errors into "ValidationObserver", to be distributed by name
            this.$refs.observerRef.errors[error] = err.response.data.accounts[0][error] ? this.$refs.observerRef.errors[error].concat(err.response.data.accounts[0][error]) : this.$refs.observerRef.errors[error].concat(err.response.data[error])
          }
        })
    },
    viewCustomer() {
      this.$router.push({ name: 'CustomerList' })
    },
    createCustomer() {
      this.$bvModal.hide('modal')
    },
    getMerchantAccounts() {
      this.axios
        .get(endpoints.listMerchantAccounts(this.$route.params.merchantID), {
          params: { page: 1, page_size: 10000 }
        })
        .then(res => {
          const accounts = res.data.results
          if (accounts.length === 1) {
            this.merchant_account = accounts[0].id
            this.show_merchant = false
          } else {
            let results = [{ value: null, text: 'Select a Merchant Account' }]
            for (const account of accounts) {
              results.push({ value: account.id, text: account.location_name })
            }
            this.merchant_accounts = results
            this.show_merchant = true
          }
        })
        .catch(err => {
          console.log('error', err)
        })
    }
  },
  computed: {
    phoneRules() {
      return this.sms_phone_number ? 'required|lessThan:17|greaterThan:14' : ''
    }
  }
}
</script>

<style src="vue-tel-input/dist/vue-tel-input.css"></style>

<style lang="scss" scoped>
.btn-group-vertical > button {
  margin-bottom: 10px;
  border-radius: 5px !important;
}
</style>
